import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { formatearFechaCompleta } from '../utils/dateUtils';
import {
  Box,
  Container,
  VStack,
  Heading,
  Text,
  Button,
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Card,
  CardHeader,
  CardBody,
  Stack,
  StackDivider,
  Badge,
  useToast,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  FormControl,
  FormLabel,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Divider,
  IconButton,
  Image,
  Flex,
  Input,
  Tooltip,
  Icon,
  HStack,
  Progress,
  SimpleGrid,
  Center,
  Circle,
} from '@chakra-ui/react';
import { FiArrowLeft, FiSend, FiDownload, FiUpload, FiInfo, FiTrash2, FiCheck, FiClock, FiX, FiUser, FiMapPin, FiSettings } from 'react-icons/fi';
import Barcode from 'react-barcode';
import {
  Expediente,
  consultarExpediente,
  consultarExpedienteCompleto,
  actualizarExpediente,
  agregarComunicacionInterna,
  userService,
  documentoService
} from '../config/index';

const DetalleExpediente: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [expediente, setExpediente] = useState<Expediente | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [informacionDenunciante, setInformacionDenunciante] = useState('');
  const [mensajeInterno, setMensajeInterno] = useState('');
  const { isOpen: isUpdateModalOpen, onOpen: onUpdateModalOpen, onClose: onUpdateModalClose } = useDisclosure();
  const { isOpen: isImageModalOpen, onOpen: onImageModalOpen, onClose: onImageModalClose } = useDisclosure();
  const [selectedImage, setSelectedImage] = useState<string | undefined>(undefined);
  const [currentUser, setCurrentUser] = useState<string>('');
  const [isSystemUser, setIsSystemUser] = useState(false);
  const [tempEstado, setTempEstado] = useState<string>('');
  const [tempDepartamento, setTempDepartamento] = useState<'Arquitecto' | 'Jurídico' | 'Administrativo'>('Administrativo');
  const [tempTipoBarrera, setTempTipoBarrera] = useState<string>('urbanistica:');
  const [tempUbicacionBarrera, setTempUbicacionBarrera] = useState<string>('');
  const [nombre, setNombre] = useState('');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [documentosExpediente, setDocumentosExpediente] = useState<Array<{
    id: string;
    nombre: string;
    descripcion: string;
    url: string;
    tipo: string;
    tamaño: number;
    fechaSubida: string;
    userId: string;
    nombreUsuario: string;
    eliminado?: boolean;
    fechaEliminacion?: string;
    eliminadoPor?: string;
    nombreOriginal?: string;
  }>>([]);
  const [loadingDocumentos, setLoadingDocumentos] = useState(false);
  const toast = useToast();
  const brandRed = useColorModeValue('red.500', 'red.300');

  // Usar función unificada de formateo de fechas
  const formatearFecha = formatearFechaCompleta;

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' bytes';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  // Función para cargar documentos del expediente
  const cargarDocumentosExpediente = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoadingDocumentos(true);
      const documentos = await documentoService.obtenerDocumentosPorExpediente(id);
      setDocumentosExpediente(documentos);
    } catch (error) {
      console.error('Error al cargar documentos del expediente:', error);
    } finally {
      setLoadingDocumentos(false);
    }
  }, [id]);

  useEffect(() => {
    const cargarExpediente = async () => {
      try {
        if (!id) {
          setError('ID de expediente no proporcionado');
          setLoading(false);
          return;
        }

        // Verificar el estado de autenticación primero
        const user = await userService.getCurrentUser();
        const isSystemUser = !!user?.role;
        
        if (user) {
          setCurrentUser(user.email);
          setIsSystemUser(isSystemUser);
        }

        // Usar la función correcta según el tipo de usuario
        const expedienteData = isSystemUser
          ? await consultarExpedienteCompleto(id)  // Incluye comunicaciones internas
          : await consultarExpediente(id);         // Versión pública sin comunicaciones internas
        
        if (!expedienteData) {
          setError('Expediente no encontrado');
        } else {
          setExpediente(expedienteData);
          setTempEstado(expedienteData.estado);
          setTempDepartamento(expedienteData.Departamento);
          
          // Cargar documentos del expediente si es usuario del sistema
          if (isSystemUser) {
            cargarDocumentosExpediente();
          }
        }
      } catch (err) {
        setError('Error al cargar el expediente');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    cargarExpediente();
  }, [id, navigate, cargarDocumentosExpediente]);

  const getEstadoBadge = (estado: string) => {
    let colorScheme = 'gray';
    switch (estado.toLowerCase()) {
      case 'en trámite':
        colorScheme = 'orange';
        break;
      case 'resuelto':
        colorScheme = 'green';
        break;
      case 'rechazado':
        colorScheme = 'gray';
        break;
    }
    return <Badge colorScheme={colorScheme}>{estado}</Badge>;
  };

  const handleOpenUpdateModal = () => {
    if (expediente) {
      setTempEstado(expediente.estado);
      setTempDepartamento(expediente.Departamento);
      
      // Determinar el tipo de barrera (urbanística o edificación)
      const tipoBarreraDetalle = expediente.tipoBarreraDetalle || '';
      const ubicacionBarrera = expediente.ubicacionBarrera || '';
      
      // Lista de tipos de barreras de edificación
      const tiposEdificacion = [
        'Escaleras',
        'Ascensor',
        'Puertas de acceso a edificios',
        'Bordillos',
        'Rampa',
        'Salvaescaleras',
        'Telefonillos',
        'Barandillas',
        'Plazas de aparcamiento',
        'Entradas a garaje'
      ];
      
      // Lista de ubicaciones de edificación
      const ubicacionesEdificacion = [
        'Edificio residencial',
        'Edificio titularidad pública',
        'Garaje o Parking',
        'Centro Sanitario u Hospitalario',
        'Local comercial, ocio o restauración',
        'Lugar de culto',
        'Centro Educativo',
        'Centro o zona deportiva'
      ];
      
      // Determinar si es una barrera de edificación basado en el tipo o ubicación
      const esBarreraEdificacion = 
        tiposEdificacion.some(tipo => tipoBarreraDetalle.includes(tipo)) ||
        ubicacionesEdificacion.some(ubicacion => ubicacionBarrera.includes(ubicacion));
      
      // Lista de tipos de barreras de transporte
      const tiposTransporte = [
        'Metro',
        'Taxi',
        'VTC',
        'EMT',
        'Autobuses Interurbanos',
        'Renfe',
        'Cercanías',
        'Estaciones',
        'Intercambiador',
        'Paradas de Autobuses'
      ];
      
      // Lista de ubicaciones de transporte
      const ubicacionesTransporte = [
        'Estación de transporte',
        'Parada de transporte',
        'Medio de transporte'
      ];
      
      // Determinar si es una barrera de transporte basado en el tipo o ubicación
      const esBarreraTransporte = 
        tiposTransporte.some(tipo => tipoBarreraDetalle.includes(tipo)) ||
        ubicacionesTransporte.some(ubicacion => ubicacionBarrera.includes(ubicacion));
      
      // Si ya tiene un prefijo, mantenerlo
      if (tipoBarreraDetalle.startsWith('urbanistica:') || tipoBarreraDetalle.startsWith('edificacion:') || tipoBarreraDetalle.startsWith('transporte:')) {
        setTempTipoBarrera(tipoBarreraDetalle);
      } else if (esBarreraTransporte) {
        // Si parece ser una barrera de transporte
        setTempTipoBarrera(`transporte:${tipoBarreraDetalle}`);
      } else if (esBarreraEdificacion) {
        // Si parece ser una barrera de edificación
        setTempTipoBarrera(`edificacion:${tipoBarreraDetalle}`);
      } else {
        // Por defecto, asumir que es urbanística
        setTempTipoBarrera(`urbanistica:${tipoBarreraDetalle}`);
      }
      
      setTempUbicacionBarrera(ubicacionBarrera);
      setInformacionDenunciante('');
    }
    onUpdateModalOpen();
  };

  const handleOpenImageModal = useCallback((imageUrl: string) => {
    setSelectedImage(imageUrl);
    onImageModalOpen();
  }, [onImageModalOpen]);

  const handleDownloadImage = useCallback((imageUrl: string | undefined, isBarreraImage: boolean) => {
    if (!imageUrl || !expediente) {
      toast({
        title: 'Error',
        description: 'La URL de la imagen no está disponible',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = isBarreraImage 
      ? `imagen-barrera-${expediente.id}.jpg` 
      : `imagen-solicitante-${expediente.id}.jpg`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
    }, 100);
    
    toast({
      title: 'Descarga iniciada',
      description: 'La imagen se está descargando',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  }, [expediente, toast]);

  if (loading) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="xl" color={brandRed} />
      </Box>
    );
  }

  return (
    <Box 
      minH="100vh" 
      backgroundImage="url('/silla.jpg')"
      backgroundSize="cover"
      backgroundPosition="center"
      position="relative"
    >
      {/* Modal para actualizar el estado del expediente */}
      <Modal isOpen={isUpdateModalOpen} onClose={onUpdateModalClose} isCentered size="xl">
        <ModalOverlay />
        <ModalContent maxH="90vh" bg="white">
          <ModalHeader 
            borderBottom="1px" 
            borderColor="gray.200" 
            bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            color="white"
            py={6}
          >
            <HStack spacing={3}>
              <Circle size="40px" bg="whiteAlpha.200">
                <Icon as={FiSettings} boxSize={5} />
              </Circle>
              <VStack align="start" spacing={0}>
                <Text fontSize="xl" fontWeight="bold">
                  Actualizar Expediente
                </Text>
                <Text fontSize="sm" opacity={0.9}>
                  {expediente?.id || 'Cargando...'}
                </Text>
              </VStack>
            </HStack>
          </ModalHeader>
          <ModalBody 
            overflowY="scroll" 
            maxH="calc(90vh - 140px)"
            css={{
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c1c1c1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#a8a8a8',
              },
            }}
            p={0}
          >
            <VStack spacing={0} align="stretch">
              {/* Sección Estado y Departamento */}
              <Box bg="linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)" p={6} borderBottom="1px" borderColor="gray.200">
                <VStack spacing={6} align="stretch">
                  <Box>
                    <HStack spacing={3} mb={4}>
                      <Circle size="32px" bg="blue.500" color="white">
                        <Icon as={FiClock} boxSize={4} />
                      </Circle>
                      <Text fontSize="lg" fontWeight="bold" color="blue.700">
                        Estado del Expediente
                      </Text>
                    </HStack>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                      {[
                        { value: 'En trámite', icon: FiClock, color: 'orange' },
                        { value: 'Resuelto', icon: FiCheck, color: 'green' },
                        { value: 'Rechazado', icon: FiX, color: 'red' }
                      ].map((estadoOption) => (
                        <Button
                          key={estadoOption.value}
                          colorScheme={tempEstado === estadoOption.value ? estadoOption.color : "gray"}
                          variant={tempEstado === estadoOption.value ? "solid" : "outline"}
                          size="lg"
                          onClick={() => setTempEstado(estadoOption.value)}
                          borderRadius="xl"
                          py={6}
                          _hover={{
                            transform: 'translateY(-2px)',
                            boxShadow: 'lg'
                          }}
                          transition="all 0.2s"
                          leftIcon={<Icon as={estadoOption.icon} boxSize={5} />}
                        >
                          {estadoOption.value}
                        </Button>
                      ))}
                    </SimpleGrid>
                  </Box>
                  
                  <Box>
                    <HStack spacing={3} mb={4}>
                      <Circle size="32px" bg="green.500" color="white">
                        <Icon as={FiUser} boxSize={4} />
                      </Circle>
                      <Text fontSize="lg" fontWeight="bold" color="green.700">
                        Departamento Asignado
                      </Text>
                    </HStack>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                      {[
                        { value: "Administrativo", icon: "📋", color: "blue" },
                        { value: "Arquitecto", icon: "🏗️", color: "purple" },
                        { value: "Jurídico", icon: "⚖️", color: "teal" }
                      ].map((depOption) => (
                        <Button
                          key={depOption.value}
                          colorScheme={tempDepartamento === depOption.value ? depOption.color : "gray"}
                          variant={tempDepartamento === depOption.value ? "solid" : "outline"}
                          size="lg"
                          onClick={() => setTempDepartamento(depOption.value as any)}
                          borderRadius="xl"
                          py={6}
                          _hover={{
                            transform: 'translateY(-2px)',
                            boxShadow: 'lg'
                          }}
                          transition="all 0.2s"
                          leftIcon={<Text fontSize="xl">{depOption.icon}</Text>}
                        >
                          {depOption.value}
                        </Button>
                      ))}
                    </SimpleGrid>
                  </Box>
                </VStack>
              </Box>
              
              {/* Sección Tipo de Barrera */}
              <Box bg="orange.50" p={6} borderBottom="1px" borderColor="gray.200">
                <VStack spacing={4} align="stretch">
                  <Box>
                    <Text fontSize="lg" fontWeight="bold" color="orange.700" mb={3}>
                      Tipo Principal de Barrera
                    </Text>
                    <Stack direction="row" spacing={3} wrap="wrap">
                      {[
                        { value: 'urbanistica', label: 'Barrera Urbanística', icon: '🏙️' },
                        { value: 'edificacion', label: 'Barrera de Edificación', icon: '🏢' },
                        { value: 'transporte', label: 'Barrera de Transporte', icon: '🚌' }
                      ].map((option) => (
                        <Button
                          key={option.value}
                          colorScheme={tempTipoBarrera.startsWith(option.value) ? "orange" : "gray"}
                          variant={tempTipoBarrera.startsWith(option.value) ? "solid" : "outline"}
                          size="lg"
                          onClick={() => {
                            setTempTipoBarrera(`${option.value}:`);
                            setTempUbicacionBarrera('');
                          }}
                          borderRadius="xl"
                          px={6}
                          py={4}
                          _hover={{
                            transform: 'translateY(-2px)',
                            boxShadow: 'lg'
                          }}
                          transition="all 0.2s"
                          leftIcon={<Text fontSize="xl">{option.icon}</Text>}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </Stack>
                  </Box>
                </VStack>
              </Box>
              
              {tempTipoBarrera.startsWith('urbanistica:') && (
                <Box bg="purple.50" p={6} borderBottom="1px" borderColor="gray.200">
                  <Text fontSize="md" fontWeight="bold" color="purple.700" mb={4}>
                    Tipo Específico de Barrera Urbanística
                  </Text>
                  <Stack spacing={2} direction="row" wrap="wrap">
                    {[
                      'Aceras',
                      'Rampas',
                      'Bordillos',
                      'Plazas PMR',
                      'Contenedores (Basura)',
                      'Semáforos',
                      'Pasos de peatones',
                      'Pavimentos',
                      'Parques infantiles (Columpios, etc)',
                      'Tapas alcantarilla',
                      'Barreras en aceras (Arboles, papeleras, farolas, etc)',
                      'Ascensor en vía pública',
                      'Otros'
                    ].map((tipoOption) => (
                      <Button
                        key={tipoOption}
                        colorScheme={tempTipoBarrera === `urbanistica:${tipoOption}` ? "purple" : "gray"}
                        variant={tempTipoBarrera === `urbanistica:${tipoOption}` ? "solid" : "outline"}
                        size="sm"
                        onClick={() => setTempTipoBarrera(`urbanistica:${tipoOption}`)}
                        borderRadius="md"
                        _hover={{
                          transform: 'translateY(-1px)',
                          boxShadow: 'sm'
                        }}
                        transition="all 0.2s"
                      >
                        {tipoOption}
                      </Button>
                    ))}
                  </Stack>
                </Box>
              )}
              
              {tempTipoBarrera.startsWith('edificacion:') && (
                <FormControl>
                  <FormLabel>Tipo de Barrera de Edificación</FormLabel>
                  <Stack spacing={2}>
                    {[
                      'Escaleras',
                      'Ascensor',
                      'Puertas de acceso a edificios',
                      'Bordillos',
                      'Rampa',
                      'Salvaescaleras',
                      'Telefonillos',
                      'Barandillas',
                      'Plazas de aparcamiento',
                      'Entradas a garaje',
                      'Otros'
                    ].map((tipoOption) => (
                      <Button
                        key={tipoOption}
                        colorScheme={tempTipoBarrera === `edificacion:${tipoOption}` ? "blue" : "gray"}
                        variant={tempTipoBarrera === `edificacion:${tipoOption}` ? "solid" : "outline"}
                        size="sm"
                        onClick={() => setTempTipoBarrera(`edificacion:${tipoOption}`)}
                      >
                        {tipoOption}
                      </Button>
                    ))}
                  </Stack>
                </FormControl>
              )}
              
              {tempTipoBarrera.startsWith('transporte:') && (
                <FormControl>
                  <FormLabel>Tipo de Barrera de Transporte</FormLabel>
                  <Stack spacing={2}>
                    {[
                      'Metro',
                      'Taxi',
                      'VTC',
                      'EMT',
                      'Autobuses Interurbanos',
                      'Renfe',
                      'Cercanías',
                      'Estaciones',
                      'Intercambiador',
                      'Paradas de Autobuses',
                      'Otros'
                    ].map((tipoOption) => (
                      <Button
                        key={tipoOption}
                        colorScheme={tempTipoBarrera === `transporte:${tipoOption}` ? "blue" : "gray"}
                        variant={tempTipoBarrera === `transporte:${tipoOption}` ? "solid" : "outline"}
                        size="sm"
                        onClick={() => setTempTipoBarrera(`transporte:${tipoOption}`)}
                      >
                        {tipoOption}
                      </Button>
                    ))}
                  </Stack>
                </FormControl>
              )}
              
              {tempTipoBarrera.startsWith('urbanistica:') && (
                <Box bg="linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)" p={6} borderBottom="1px" borderColor="gray.200">
                  <VStack spacing={4} align="stretch">
                    <Box>
                      <HStack spacing={3} mb={4}>
                        <Circle size="32px" bg="purple.500" color="white">
                          <Icon as={FiMapPin} boxSize={4} />
                        </Circle>
                        <Text fontSize="md" fontWeight="bold" color="purple.700">
                          Ubicación de la Barrera Urbanística
                        </Text>
                      </HStack>
                      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
                        {[
                          { value: 'Vía pública', icon: '🛣️', description: 'Calles y avenidas' },
                          { value: 'Carretera', icon: '🛤️', description: 'Vías interurbanas' },
                          { value: 'Plaza aparcamiento', icon: '🅿️', description: 'Espacios de estacionamiento' },
                          { value: 'Paso de peatones', icon: '🚶', description: 'Cruces peatonales' },
                          { value: 'Parque o jardín', icon: '🌳', description: 'Espacios verdes' },
                          { value: 'Plaza', icon: '🏛️', description: 'Espacios públicos' },
                          { value: 'Otros', icon: '📍', description: 'Otras ubicaciones' }
                        ].map((ubicacionOption) => (
                          <Button
                            key={ubicacionOption.value}
                            colorScheme={tempUbicacionBarrera === ubicacionOption.value ? "purple" : "gray"}
                            variant={tempUbicacionBarrera === ubicacionOption.value ? "solid" : "outline"}
                            size="md"
                            onClick={() => setTempUbicacionBarrera(ubicacionOption.value)}
                            borderRadius="xl"
                            py={6}
                            px={4}
                            _hover={{
                              transform: 'translateY(-2px)',
                              boxShadow: 'lg'
                            }}
                            transition="all 0.2s"
                            height="auto"
                            whiteSpace="normal"
                            textAlign="center"
                          >
                            <VStack spacing={1}>
                              <Text fontSize="2xl">{ubicacionOption.icon}</Text>
                              <Text fontSize="sm" fontWeight="bold">
                                {ubicacionOption.value}
                              </Text>
                              <Text fontSize="xs" opacity={0.8}>
                                {ubicacionOption.description}
                              </Text>
                            </VStack>
                          </Button>
                        ))}
                      </SimpleGrid>
                    </Box>
                  </VStack>
                </Box>
              )}
              
              {tempTipoBarrera.startsWith('edificacion:') && (
                <FormControl>
                  <FormLabel>Ubicación de la Barrera de Edificación</FormLabel>
                  <Stack spacing={2}>
                    {[
                      'Edificio residencial',
                      'Edificio titularidad pública',
                      'Garaje o Parking',
                      'Centro Sanitario u Hospitalario',
                      'Local comercial, ocio o restauración',
                      'Lugar de culto',
                      'Centro Educativo',
                      'Centro o zona deportiva',
                      'Otros'
                    ].map((ubicacionOption) => (
                      <Button
                        key={ubicacionOption}
                        colorScheme={tempUbicacionBarrera === ubicacionOption ? "blue" : "gray"}
                        variant={tempUbicacionBarrera === ubicacionOption ? "solid" : "outline"}
                        size="sm"
                        onClick={() => setTempUbicacionBarrera(ubicacionOption)}
                      >
                        {ubicacionOption}
                      </Button>
                    ))}
                  </Stack>
                </FormControl>
              )}
              
              {tempTipoBarrera.startsWith('transporte:') && (
                <FormControl>
                  <FormLabel>Ubicación de la Barrera de Transporte</FormLabel>
                  <Stack spacing={2}>
                    {[
                      'Estación de transporte',
                      'Parada de transporte',
                      'Medio de transporte',
                      'Otros'
                    ].map((ubicacionOption) => (
                      <Button
                        key={ubicacionOption}
                        colorScheme={tempUbicacionBarrera === ubicacionOption ? "blue" : "gray"}
                        variant={tempUbicacionBarrera === ubicacionOption ? "solid" : "outline"}
                        size="sm"
                        onClick={() => setTempUbicacionBarrera(ubicacionOption)}
                      >
                        {ubicacionOption}
                      </Button>
                    ))}
                  </Stack>
                </FormControl>
              )}
              
              {/* Sección Información para el Denunciante */}
              <Box bg="linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%)" p={6} borderBottom="1px" borderColor="gray.200">
                <VStack spacing={4} align="stretch">
                  <Box>
                    <HStack spacing={3} mb={4}>
                      <Circle size="32px" bg="green.500" color="white">
                        <Icon as={FiInfo} boxSize={4} />
                      </Circle>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="md" fontWeight="bold" color="green.700">
                          Información para el Denunciante
                        </Text>
                        <Text fontSize="xs" color="green.600" opacity={0.8}>
                          Mensaje que será visible para el ciudadano
                        </Text>
                      </VStack>
                    </HStack>
                    <Box>
                      <Textarea
                        value={informacionDenunciante}
                        onChange={(e) => setInformacionDenunciante(e.target.value)}
                        placeholder="Escriba aquí la información que desea comunicar al denunciante sobre el estado o progreso de su expediente..."
                        size="md"
                        resize="vertical"
                        rows={4}
                        bg="white"
                        borderColor="green.200"
                        _hover={{ borderColor: "green.300" }}
                        _focus={{ 
                          borderColor: "green.400",
                          boxShadow: "0 0 0 1px var(--chakra-colors-green-400)"
                        }}
                        borderRadius="xl"
                        p={4}
                        fontSize="sm"
                      />
                      <HStack spacing={2} mt={2} justify="space-between">
                        <Text fontSize="xs" color="green.600">
                          💡 Esta información aparecerá en el historial del expediente
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {informacionDenunciante.length}/500 caracteres
                        </Text>
                      </HStack>
                    </Box>
                  </Box>
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          
          <ModalFooter borderTop="1px" borderColor="gray.200" bg="gray.50">
            <Button variant="ghost" mr={3} onClick={onUpdateModalClose}>
              Cancelar
            </Button>
            <Button 
              colorScheme="blue"
              onClick={async () => {
                try {
                  if (!expediente) return;
                  
                  // Obtener el tipo de barrera actual con formato comparable
                  const tipoBarreraActual = expediente.tipoBarreraDetalle || '';
                  const ubicacionBarreraActual = expediente.ubicacionBarrera || '';
                  
                  // Verificar si hay cambios
                  const hayInformacionDenunciante = informacionDenunciante && informacionDenunciante.trim().length > 0;
                  
                  // Normalizar el tipo de barrera actual para comparación
                  let tipoBarreraActualNormalizado = tipoBarreraActual;
                  if (tipoBarreraActual && !tipoBarreraActual.includes(':')) {
                    // Si el tipo actual no tiene prefijo, intentar determinarlo
                    const tiposEdificacion = [
                      'Escaleras', 'Ascensor', 'Puertas de acceso a edificios', 'Bordillos',
                      'Rampa', 'Salvaescaleras', 'Telefonillos', 'Barandillas',
                      'Plazas de aparcamiento', 'Entradas a garaje'
                    ];
                    const tiposTransporte = [
                      'Metro', 'Taxi', 'VTC', 'EMT', 'Autobuses Interurbanos',
                      'Renfe', 'Cercanías', 'Estaciones', 'Intercambiador', 'Paradas de Autobuses'
                    ];
                    
                    if (tiposTransporte.some(tipo => tipoBarreraActual.includes(tipo))) {
                      tipoBarreraActualNormalizado = `transporte:${tipoBarreraActual}`;
                    } else if (tiposEdificacion.some(tipo => tipoBarreraActual.includes(tipo))) {
                      tipoBarreraActualNormalizado = `edificacion:${tipoBarreraActual}`;
                    } else {
                      tipoBarreraActualNormalizado = `urbanistica:${tipoBarreraActual}`;
                    }
                  }
                  
                  const haycambiosEnCampos = (
                    tempEstado !== expediente.estado ||
                    tempDepartamento !== expediente.Departamento ||
                    tempTipoBarrera !== tipoBarreraActualNormalizado ||
                    tempUbicacionBarrera !== ubicacionBarreraActual
                  );
                  
                  if (!haycambiosEnCampos && !hayInformacionDenunciante) {
                    toast({
                      title: "Sin cambios",
                      description: "No has realizado ningún cambio en el expediente",
                      status: "info",
                      duration: 3000,
                      isClosable: true,
                    });
                    return;
                  }
                  
                  // Actualizar el expediente
                  await actualizarExpediente(
                    expediente.id,
                    {
                      estado: tempEstado,
                      Departamento: tempDepartamento,
                      tipoBarreraDetalle: tempTipoBarrera || undefined,
                      ubicacionBarrera: tempUbicacionBarrera || undefined
                    },
                    currentUser || 'Sistema',
                    informacionDenunciante || undefined
                  );
                  
                  // Recargar el expediente para mostrar los cambios (usar función completa para usuarios del sistema)
                  const expedienteActualizado = await consultarExpedienteCompleto(expediente.id);
                  if (expedienteActualizado) {
                    setExpediente(expedienteActualizado);
                  }
                  
                  // Cerrar el modal y mostrar mensaje de éxito
                  onUpdateModalClose();
                  toast({
                    title: "Expediente actualizado",
                    description: "Los cambios se han guardado correctamente",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                  });
                } catch (error) {
                  console.error('Error al actualizar expediente:', error);
                  toast({
                    title: "Error",
                    description: "No se pudo actualizar el expediente",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                  });
                }
              }}
            >
              Guardar Cambios
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      {/* Modal para visualizar imágenes a tamaño completo */}
      <Modal isOpen={isImageModalOpen} onClose={onImageModalClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent maxW="90vw" maxH="90vh">
          <ModalHeader p={3} fontSize="md">
            Imagen a tamaño completo
            <IconButton
              icon={<FiDownload />}
              aria-label="Descargar imagen"
              size="sm"
              colorScheme="blue"
              ml={3}
              onClick={() => {
                if (!selectedImage) return;
                
                const isBarreraImage = selectedImage.includes('imagenBarrera');
                handleDownloadImage(selectedImage, isBarreraImage);
              }}
            />
          </ModalHeader>
          <ModalBody p={2} display="flex" justifyContent="center" alignItems="center">
            {selectedImage && (
              <Image
                src={selectedImage}
                maxH="80vh"
                maxW="100%"
                objectFit="contain"
                borderRadius="md"
              />
            )}
          </ModalBody>
          <ModalFooter p={2}>
            <Button onClick={onImageModalClose}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="whiteAlpha.700"
      />
      <Container maxW="container.md" position="relative" zIndex={1} py={8}>
        <VStack spacing={6} align="stretch">
          <Box 
            position="relative"
            bg="whiteAlpha.500"
            p={{ base: 2, md: 3 }}
            borderRadius="xl"
            backdropFilter="blur(10px)"
            display="flex"
            alignItems="center"
            minH={{ base: "60px", md: "70px" }}
          >
            <Link to="/">
              <Box 
                as="img" 
                src="/famma.png" 
                alt="Logo Famma" 
                className="logo" 
                h={{ base: "45px", md: "55px" }}
                objectFit="contain"
                transition="transform 0.2s ease"
                _hover={{ transform: 'scale(1.05)' }}
                ml={{ base: 2, md: 3 }}
              />
            </Link>
            <Box flex="1" textAlign="center">
              <Heading
                fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
                fontWeight="bold"
                color="brand.600"
                letterSpacing="tight"
                lineHeight="shorter"
              >
                Detalle del Expediente
              </Heading>
            </Box>
            <Box w={{ base: "45px", md: "55px" }} /> {/* Spacer para balancear el logo */}
          </Box>

          <Stack direction="row" spacing={4} w="100%">
            <Button
              leftIcon={<FiArrowLeft />}
              variant="ghost"
              onClick={() => navigate('/dashboard')}
            >
              Volver al Dashboard
            </Button>
            <Button
              onClick={() => window.print()}
              colorScheme="blue"
              display={{ base: 'none', print: 'block' }}
            >
              Imprimir Expediente
            </Button>
          </Stack>

          {error ? (
            <Alert status="error">
              <AlertIcon />
              {error}
            </Alert>
          ) : expediente ? (
            <Card bg="white" shadow="sm">
              <CardHeader>
                <VStack align="stretch" spacing={4}>
                  <Heading size="lg" color={brandRed}>
                    Expediente {expediente.id}
                  </Heading>
                  <Box display="flex" justifyContent="center" p={4} bg="white">
                    <Barcode 
                      value={expediente.id}
                      width={1.5}
                      height={50}
                      fontSize={14}
                    />
                  </Box>
                </VStack>
              </CardHeader>

              <CardBody>
                <Tabs variant="enclosed" colorScheme="blue">
                  <TabList>
                    <Tab>Información General</Tab>
                    <Tab>Solicitante</Tab>
                    <Tab>Evolución</Tab>
                    {isSystemUser && <Tab>Comunicación Interna</Tab>}
                    {isSystemUser && <Tab>Documentos</Tab>}
                  </TabList>

                  <TabPanels>
                    <TabPanel>
                      <Stack divider={<StackDivider />} spacing={4}>
                        <Box>
                          <Text color="gray.500" fontSize="sm">
                            Estado
                          </Text>
                          <Stack direction="row" align="center" spacing={4}>
                            {getEstadoBadge(expediente.estado)}
                            {isSystemUser && (
                              <Button
                                size="sm"
                                onClick={handleOpenUpdateModal}
                              >
                                Actualizar Expediente
                              </Button>
                            )}
                          </Stack>
                        </Box>

                        <Box>
                          <Text color="gray.500" fontSize="sm">
                            Fecha de Creación
                          </Text>
                          <Text>{formatearFecha(expediente.fechaCreacion)}</Text>
                        </Box>

                        <Box>
                          <Text color="gray.500" fontSize="sm">
                            Tipo de Barrera
                          </Text>
                          <Text>{expediente.tipoBarrera}</Text>
                        </Box>

                        {expediente.tipoBarreraDetalle && (
                          <Box>
                            <Text color="gray.500" fontSize="sm">
                              Tipo de Barrera
                            </Text>
                            <Text>
                              {(() => {
                                const tipoBarrera = expediente.tipoBarreraDetalle || '';
                                if (tipoBarrera.startsWith('urbanistica:')) {
                                  return `Urbanística: ${tipoBarrera.replace('urbanistica:', '')}`;
                                } else if (tipoBarrera.startsWith('edificacion:')) {
                                  return `Edificación: ${tipoBarrera.replace('edificacion:', '')}`;
                                } else if (tipoBarrera.startsWith('transporte:')) {
                                  return `Transporte: ${tipoBarrera.replace('transporte:', '')}`;
                                } else {
                                  return tipoBarrera;
                                }
                              })()}
                            </Text>
                          </Box>
                        )}

                        {expediente.ubicacionBarrera && (
                          <Box>
                            <Text color="gray.500" fontSize="sm">
                              Ubicación de la Barrera
                            </Text>
                            <Text>
                              {(() => {
                                const tipoBarrera = expediente.tipoBarreraDetalle || '';
                                const ubicacion = expediente.ubicacionBarrera || '';
                                
                                if (tipoBarrera.startsWith('urbanistica:')) {
                                  return `Urbanística: ${ubicacion}`;
                                } else if (tipoBarrera.startsWith('edificacion:')) {
                                  return `Edificación: ${ubicacion}`;
                                } else if (tipoBarrera.startsWith('transporte:')) {
                                  return `Transporte: ${ubicacion}`;
                                } else {
                                  return ubicacion;
                                }
                              })()}
                            </Text>
                          </Box>
                        )}

                        <Box>
                          <Text color="gray.500" fontSize="sm">
                            Departamento Asignado
                          </Text>
                          <Text>{expediente.Departamento}</Text>
                        </Box>

                        <Box>
                          <Text color="gray.500" fontSize="sm">
                            Descripción
                          </Text>
                          <Text>{expediente.descripcion}</Text>
                        </Box>
                      </Stack>
                    </TabPanel>

                    <TabPanel>
                      <Stack spacing={6}>
                        {expediente.imagenUrl && expediente.imagenUrl.trim() !== '' && (
                          <Box>
                            <Flex justify="space-between" align="center" mb={2}>
                              <Text color="gray.500" fontSize="sm">
                                Imagen del Solicitante
                              </Text>
                              <IconButton
                                icon={<FiDownload />}
                                aria-label="Descargar imagen"
                                size="sm"
                                colorScheme="blue"
                                variant="ghost"
                                title="Descargar imagen"
                                onClick={() => handleDownloadImage(expediente.imagenUrl, false)}
                              />
                            </Flex>
                            <Box 
                              maxW="300px" 
                              borderRadius="md" 
                              overflow="hidden"
                              boxShadow="md"
                              cursor="pointer"
                              onClick={() => expediente.imagenUrl && handleOpenImageModal(expediente.imagenUrl)}
                              _hover={{ opacity: 0.9 }}
                              position="relative"
                            >
                              <Image 
                                src={expediente.imagenUrl} 
                                alt="Imagen del solicitante"
                                width="100%"
                                height="auto"
                                objectFit="cover"
                                fallback={
                                  <Box 
                                    width="100%" 
                                    height="150px" 
                                    bg="gray.100" 
                                    display="flex" 
                                    flexDirection="column"
                                    alignItems="center" 
                                    justifyContent="center"
                                    gap={2}
                                  >
                                    <Text color="gray.500">Imagen no disponible</Text>
                                    <Text color="gray.400" fontSize="xs" textAlign="center" px={2}>
                                      {expediente.imagenUrl?.startsWith('https://firebasestorage') 
                                        ? 'La imagen estaba en Firebase y la URL puede haber expirado.'
                                        : expediente.imagenUrl?.startsWith('data:image')
                                        ? 'La imagen está guardada pero no carga correctamente.'
                                        : 'No hay URL de imagen válida guardada.'}
                                    </Text>
                                  </Box>
                                }
                              />
                              <Box 
                                position="absolute" 
                                top={0} 
                                left={0} 
                                right={0} 
                                bottom={0} 
                                display="flex" 
                                alignItems="center" 
                                justifyContent="center" 
                                opacity={0} 
                                bg="blackAlpha.200" 
                                _hover={{ opacity: 1 }}
                                transition="opacity 0.2s"
                              >
                                <Text color="white" fontWeight="bold" fontSize="sm" bg="blackAlpha.700" p={2} borderRadius="md">
                                  Ver a tamaño completo
                                </Text>
                              </Box>
                            </Box>
                          </Box>
                        )}
                        <Stack spacing={4} divider={<StackDivider />}>
                          <Box>
                            <Text fontSize="xl" color="red.500" mb={4}>Información Personal</Text>
                            <Stack spacing={3}>
                              <Box>
                                <Text color="gray.600" fontSize="md">
                                  Nombre y Apellidos
                                </Text>
                                <Text>{expediente.nombreApellidos || '-'}</Text>
                              </Box>
                              <Box>
                                <Text color="gray.600" fontSize="md">
                                  Email
                                </Text>
                                <Text>{expediente.email || '-'}</Text>
                              </Box>
                              <Box>
                                <Text color="gray.600" fontSize="md">
                                  Teléfono Fijo
                                </Text>
                                <Text>{expediente.telefonoFijo || '-'}</Text>
                              </Box>
                              <Box>
                                <Text color="gray.600" fontSize="md">
                                  Móvil
                                </Text>
                                <Text>{expediente.movil || '-'}</Text>
                              </Box>
                              <Box>
                                <Text color="gray.600" fontSize="md">
                                  Año de Nacimiento
                                </Text>
                                <Text>{expediente.fechaNacimiento || '-'}</Text>
                              </Box>
                              <Box>
                                <Text color="gray.600" fontSize="md">
                                  Género
                                </Text>
                                <Text>
                                  {expediente.genero ? expediente.genero.charAt(0).toUpperCase() + expediente.genero.slice(1) : '-'}
                                </Text>
                              </Box>
                            </Stack>
                          </Box>

                          <Box>
                            <Text fontSize="xl" color="red.500" mb={4}>Información de Discapacidad</Text>
                            <Stack spacing={3}>
                              <Box>
                                <Text color="gray.600" fontSize="md">
                                  ¿Tiene Discapacidad?
                                </Text>
                                <Text>
                                  {expediente.tipoPersona === 'tiene_discapacidad' ? 'Sí' : 
                                   expediente.tipoPersona === 'no_tiene_discapacidad' ? 'No' : '-'}
                                </Text>
                              </Box>
                              {expediente.tipoPersona === 'tiene_discapacidad' && (
                                <>
                                  <Box>
                                    <Text color="gray.600" fontSize="md">
                                      Grado de Discapacidad
                                    </Text>
                                    <Text>{expediente.gradoDiscapacidad || '-'}</Text>
                                  </Box>
                                </>
                              )}
                              <Box>
                                <Text color="gray.600" fontSize="md">
                                  Tipo de Discapacidad
                                </Text>
                                <Text>
                                  {(() => {
                                    const tipos = {
                                      fisica: "Física u orgánica",
                                      intelectual: "Intelectual",
                                      mental: "Mental",
                                      sensorial: "Sensorial"
                                    };
                                    return expediente.tipoDiscapacidad ? 
                                      tipos[expediente.tipoDiscapacidad as keyof typeof tipos] || expediente.tipoDiscapacidad
                                      : '-';
                                  })()}
                                </Text>
                              </Box>
                              <Box>
                                <Text color="gray.600" fontSize="md">
                                  Baremo de Movilidad Reducida
                                </Text>
                                <Text>
                                  {expediente.baremoMovilidad ? (expediente.baremoMovilidad === 'si' ? 'Sí' : 'No') : '-'}
                                </Text>
                              </Box>
                            </Stack>
                          </Box>

                          <Box>
                            <Text fontSize="xl" color="red.500" mb={4}>Dirección</Text>
                            <Stack spacing={3}>
                              <Box>
                                <Text color="gray.600" fontSize="md">
                                  Dirección
                                </Text>
                                <Text>{expediente.direccion || '-'}</Text>
                              </Box>
                              <Box>
                                <Text color="gray.600" fontSize="md">
                                  Localidad
                                </Text>
                                <Text>{expediente.localidad || '-'}</Text>
                              </Box>
                              <Box>
                                <Text color="gray.600" fontSize="md">
                                  Código Postal
                                </Text>
                                <Text>{expediente.codigoPostal || '-'}</Text>
                              </Box>
                              <Box>
                                <Text color="gray.600" fontSize="md">
                                  Provincia
                                </Text>
                                <Text>{expediente.provincia || '-'}</Text>
                              </Box>
                            </Stack>
                          </Box>

                          <Box>
                            <Text fontSize="xl" color="red.500" mb={4}>Información de la Barrera</Text>
                            <Stack spacing={3}>
                              {expediente.imagenBarrera && expediente.imagenBarrera.trim() !== '' && (
                                <Box>
                                  <Flex justify="space-between" align="center" mb={2}>
                                    <Text color="gray.600" fontSize="md">
                                      Imagen de la Barrera
                                    </Text>
                                    <IconButton
                                      icon={<FiDownload />}
                                      aria-label="Descargar imagen"
                                      size="sm"
                                      colorScheme="blue"
                                      variant="ghost"
                                      title="Descargar imagen"
                                      onClick={() => handleDownloadImage(expediente.imagenBarrera, true)}
                                    />
                                  </Flex>
                                  <Box 
                                    maxW="300px" 
                                    borderRadius="md" 
                                    overflow="hidden"
                                    boxShadow="md"
                                    cursor="pointer"
                                    onClick={() => expediente.imagenBarrera && handleOpenImageModal(expediente.imagenBarrera)}
                                    _hover={{ opacity: 0.9 }}
                                    position="relative"
                                  >
                                    <Image 
                                      src={expediente.imagenBarrera} 
                                      alt="Imagen de la barrera"
                                      width="100%"
                                      height="auto"
                                      objectFit="cover"
                                      fallback={
                                        <Box 
                                          width="100%" 
                                          height="150px" 
                                          bg="gray.100" 
                                          display="flex" 
                                          alignItems="center" 
                                          justifyContent="center"
                                        >
                                          <Text color="gray.500">Imagen no disponible</Text>
                                        </Box>
                                      }
                                    />
                                    <Box 
                                      position="absolute" 
                                      top={0} 
                                      left={0} 
                                      right={0} 
                                      bottom={0} 
                                      display="flex" 
                                      alignItems="center" 
                                      justifyContent="center" 
                                      opacity={0} 
                                      bg="blackAlpha.200" 
                                      _hover={{ opacity: 1 }}
                                      transition="opacity 0.2s"
                                    >
                                      <Text color="white" fontWeight="bold" fontSize="sm" bg="blackAlpha.700" p={2} borderRadius="md">
                                        Ver a tamaño completo
                                      </Text>
                                    </Box>
                                  </Box>
                                </Box>
                              )}
                              <Box>
                                <Text color="gray.600" fontSize="md">
                                  Tipo de Barrera
                                </Text>
                                <Text>{expediente.tipoBarrera || '-'}</Text>
                              </Box>
                              <Box>
                                <Text color="gray.600" fontSize="md">
                                  Descripción de la Barrera
                                </Text>
                                <Text>{expediente.descripcion || '-'}</Text>
                              </Box>
                            </Stack>
                          </Box>
                        </Stack>
                      </Stack>
                    </TabPanel>

                    <TabPanel>
                      {expediente.historial && expediente.historial.length > 0 ? (
                        <Stack spacing={4}>
                          {expediente.historial.map((cambio, index) => (
                            <Box key={index} p={4} bg="gray.50" borderRadius="md">
                              <Text fontSize="sm" color="gray.600">
                                {formatearFecha(cambio.fecha)}{isSystemUser && ` - ${cambio.usuario}`}
                              </Text>
                              <Text fontWeight="medium" whiteSpace="pre-line">{cambio.cambio}</Text>
                              {cambio.informacionDenunciante && (
                                <Text mt={2} color="blue.600" whiteSpace="pre-line">
                                  {cambio.informacionDenunciante}
                                </Text>
                              )}
                            </Box>
                          ))}
                        </Stack>
                      ) : (
                        <Text color="gray.500">No hay evolución registrada</Text>
                      )}
                    </TabPanel>

                    {isSystemUser && (
                      <TabPanel>
                        <VStack spacing={4} align="stretch">
                          <Box>
                            <FormControl>
                              <FormLabel>Nueva comunicación</FormLabel>
                              <Stack direction="row" spacing={2}>
                                <Textarea
                                  value={mensajeInterno}
                                  onChange={(e) => setMensajeInterno(e.target.value)}
                                  placeholder="Escribe un mensaje interno..."
                                  resize="none"
                                  rows={2}
                                />
                                <IconButton
                                  icon={<FiSend />}
                                  aria-label="Enviar mensaje"
                                  colorScheme="blue"
                                  onClick={async () => {
                                    if (!mensajeInterno.trim() || !expediente) return;
                                    try {
                                      await agregarComunicacionInterna(
                                        expediente.id,
                                        currentUser,
                                        mensajeInterno
                                      );
                                      // Usar la función completa para usuarios del sistema
                                      const expedienteActualizado = await consultarExpedienteCompleto(expediente.id);
                                      if (expedienteActualizado) {
                                        setExpediente(expedienteActualizado);
                                      }
                                      setMensajeInterno('');
                                      toast({
                                        title: "Mensaje enviado",
                                        status: "success",
                                        duration: 3000,
                                      });
                                    } catch (err) {
                                      console.error('Error al enviar mensaje:', err);
                                      toast({
                                        title: "Error al enviar el mensaje",
                                        status: "error",
                                        duration: 3000,
                                      });
                                    }
                                  }}
                                />
                              </Stack>
                            </FormControl>
                          </Box>
                          <Divider />
                          <Box>
                            <Stack spacing={4}>
                              {(expediente.comunicacionesInternas || []).map((comunicacion, index) => (
                                <Box key={index} p={4} bg="blue.50" borderRadius="md">
                                  <Text fontSize="sm" color="gray.600">
                                    {formatearFecha(comunicacion.fecha)} - {comunicacion.usuario}
                                  </Text>
                                  <Text mt={1}>{comunicacion.mensaje}</Text>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        </VStack>
                      </TabPanel>
                    )}
                    
                    {isSystemUser && (
                      <TabPanel>
                        <VStack spacing={4} align="stretch">
                          <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
                            <Heading size="sm" mb={4} color={brandRed}>Subir Nuevo Documento</Heading>
                            
                            <VStack spacing={4} align="stretch">
                              <FormControl isRequired>
                                <FormLabel>Nombre del documento</FormLabel>
                                <Input 
                                  placeholder="Nombre descriptivo del documento" 
                                  value={nombre}
                                  onChange={(e) => setNombre(e.target.value)}
                                />
                              </FormControl>
                              
                              <FormControl>
                                <FormLabel>Descripción</FormLabel>
                                <Textarea 
                                  placeholder="Descripción opcional del documento" 
                                  value={mensajeInterno}
                                  onChange={(e) => setMensajeInterno(e.target.value)}
                                />
                              </FormControl>
                              
                              <FormControl isRequired>
                                <FormLabel>
                                  Archivo (PDF, DOCX o imágenes, máx. 5MB)
                                  <Tooltip label="Se permiten archivos PDF, DOCX e imágenes (JPG, PNG, GIF, WEBP) con un tamaño máximo de 5MB">
                                    <span>
                                      <Icon as={FiInfo} ml={1} />
                                    </span>
                                  </Tooltip>
                                </FormLabel>
                                <Input
                                  type="file"
                                  accept=".pdf,.docx,.jpg,.jpeg,.png,.gif,.webp,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*"
                                  onChange={(e) => {
                                    if (e.target.files && e.target.files.length > 0) {
                                      const file = e.target.files[0];
                                      
                                      // Verificar tipo de archivo
                                      if (!file.type.includes('pdf') && 
                                          !file.type.includes('document') && 
                                          !file.type.includes('image/')) {
                                        toast({
                                          title: 'Tipo de archivo no permitido',
                                          description: 'Solo se permiten archivos PDF, DOCX e imágenes (JPG, PNG, GIF, WEBP)',
                                          status: 'error',
                                          duration: 5000,
                                          isClosable: true,
                                        });
                                        return;
                                      }
                                      
                                      // Verificar tamaño del archivo (5MB)
                                      if (file.size > 5 * 1024 * 1024) {
                                        toast({
                                          title: 'Archivo demasiado grande',
                                          description: 'El tamaño máximo permitido es de 5MB',
                                          status: 'error',
                                          duration: 5000,
                                          isClosable: true,
                                        });
                                        return;
                                      }
                                      
                                      setArchivo(file);
                                      
                                      // Si no hay nombre, usar el nombre del archivo
                                      if (!nombre) {
                                        setNombre(file.name.split('.')[0]);
                                      }
                                    }
                                  }}
                                  padding={1}
                                />
                                {archivo && (
                                  <Text fontSize="sm" color="green.500" mt={1}>
                                    Archivo seleccionado: {archivo.name} ({formatFileSize(archivo.size)})
                                  </Text>
                                )}
                              </FormControl>
                              
                              <Button
                                leftIcon={<FiUpload />}
                                colorScheme="red"
                                onClick={async () => {
                                  if (!archivo) {
                                    toast({
                                      title: 'Selecciona un archivo',
                                      description: 'Debes seleccionar un archivo para subir',
                                      status: 'warning',
                                      duration: 5000,
                                      isClosable: true,
                                    });
                                    return;
                                  }
                                  
                                  if (!nombre.trim()) {
                                    toast({
                                      title: 'Nombre requerido',
                                      description: 'Debes proporcionar un nombre para el documento',
                                      status: 'warning',
                                      duration: 5000,
                                      isClosable: true,
                                    });
                                    return;
                                  }
                                  
                                  try {
                                    setIsUploading(true);
                                    await documentoService.subirDocumento(archivo, nombre, mensajeInterno, expediente.id);
                                    
                                    // Recargar documentos del expediente
                                    await cargarDocumentosExpediente();
                                    
                                    // Limpiar el formulario
                                    setArchivo(null);
                                    setNombre('');
                                    setMensajeInterno('');
                                    
                                    toast({
                                      title: 'Documento subido',
                                      description: 'El documento se ha subido correctamente',
                                      status: 'success',
                                      duration: 5000,
                                      isClosable: true,
                                    });
                                  } catch (error) {
                                    console.error('Error al subir documento:', error);
                                    const message = error instanceof Error ? error.message : 'No se pudo subir el documento';
                                    toast({
                                      title: 'Error',
                                      description: message,
                                      status: 'error',
                                      duration: 5000,
                                      isClosable: true,
                                    });
                                  } finally {
                                    setIsUploading(false);
                                  }
                                }}
                                isLoading={isUploading}
                                loadingText="Subiendo..."
                                isDisabled={!archivo || !nombre.trim() || isUploading}
                              >
                                Subir Documento
                              </Button>
                            </VStack>
                          </Box>
                          
                          <Box mt={4}>
                            <Heading size="sm" mb={4} color={brandRed}>Documentos del Expediente</Heading>
                            
                            {loadingDocumentos ? (
                              <Flex justify="center" align="center" py={4}>
                                <Spinner size="md" color={brandRed} />
                                <Text ml={2}>Cargando documentos...</Text>
                              </Flex>
                            ) : documentosExpediente.length === 0 ? (
                              <Text color="gray.500" textAlign="center" py={4}>
                                No hay documentos asociados a este expediente
                              </Text>
                            ) : (
                              <VStack spacing={3} align="stretch">
                                {documentosExpediente.map((documento) => (
                                  <Box 
                                    key={documento.id} 
                                    p={3} 
                                    bg={documento.eliminado ? "red.50" : "gray.50"} 
                                    borderRadius="md" 
                                    border="1px" 
                                    borderColor={documento.eliminado ? "red.200" : "gray.200"}
                                    opacity={documento.eliminado ? 0.7 : 1}
                                  >
                                    <Flex justify="space-between" align="center">
                                      <Box flex="1">
                                        <Text 
                                          fontWeight="medium" 
                                          color={documento.eliminado ? "red.600" : brandRed}
                                          textDecoration={documento.eliminado ? "line-through" : "none"}
                                        >
                                          {documento.eliminado ? documento.nombreOriginal || documento.nombre : documento.nombre}
                                        </Text>
                                        {documento.eliminado ? (
                                          <VStack align="start" spacing={1} mt={1}>
                                            <Text fontSize="sm" color="red.600" fontWeight="medium">
                                              Documento eliminado
                                            </Text>
                                            <Text fontSize="xs" color="red.500">
                                              Eliminado por: {documento.eliminadoPor}
                                            </Text>
                                            <Text fontSize="xs" color="red.500">
                                              Fecha de eliminación: {documento.fechaEliminacion ? formatearFecha(documento.fechaEliminacion) : ''}
                                            </Text>
                                          </VStack>
                                        ) : (
                                          <>
                                            {documento.descripcion && (
                                              <Text fontSize="sm" color="gray.600" mt={1}>
                                                {documento.descripcion}
                                              </Text>
                                            )}
                                            <Text fontSize="xs" color="gray.500" mt={1}>
                                              Subido el {documento.fechaSubida} • {formatFileSize(documento.tamaño)}
                                            </Text>
                                          </>
                                        )}
                                      </Box>
                                      {!documento.eliminado && (
                                        <Stack direction="row" spacing={1}>
                                          <IconButton
                                            icon={<FiDownload />}
                                            aria-label="Descargar documento"
                                            size="sm"
                                            colorScheme="blue"
                                            variant="ghost"
                                            onClick={async () => {
                                            try {
                                              const documentData = await documentoService.obtenerDocumentoParaDescargar(documento.id);
                                              if (!documentData) {
                                                toast({
                                                  title: 'Error al descargar',
                                                  description: 'No se pudo obtener el contenido del documento',
                                                  status: 'error',
                                                  duration: 5000,
                                                  isClosable: true,
                                                });
                                                return;
                                              }
                                              
                                              const link = document.createElement('a');
                                              link.href = documentData.content;
                                              link.download = documento.nombre;
                                              document.body.appendChild(link);
                                              link.click();
                                              document.body.removeChild(link);
                                              
                                              toast({
                                                title: 'Descarga iniciada',
                                                description: 'El documento se está descargando',
                                                status: 'success',
                                                duration: 3000,
                                                isClosable: true,
                                              });
                                            } catch (error) {
                                              console.error('Error al descargar documento:', error);
                                              toast({
                                                title: 'Error al descargar',
                                                description: 'No se pudo descargar el documento',
                                                status: 'error',
                                                duration: 5000,
                                                isClosable: true,
                                              });
                                            }
                                          }}
                                        />
                                          <IconButton
                                            icon={<FiTrash2 />}
                                            aria-label="Eliminar documento"
                                            size="sm"
                                            colorScheme="red"
                                            variant="ghost"
                                            onClick={async () => {
                                              if (window.confirm('¿Estás seguro de que quieres eliminar este documento? El archivo se marcará como eliminado pero se conservará el registro.')) {
                                                try {
                                                  await documentoService.eliminarDocumento(documento.id);
                                                  
                                                  // Recargar documentos del expediente
                                                  await cargarDocumentosExpediente();
                                                  
                                                  toast({
                                                    title: 'Documento eliminado',
                                                    description: 'El documento se ha marcado como eliminado',
                                                    status: 'success',
                                                    duration: 3000,
                                                    isClosable: true,
                                                  });
                                                } catch (error) {
                                                  console.error('Error al eliminar documento:', error);
                                                  toast({
                                                    title: 'Error al eliminar',
                                                    description: error instanceof Error ? error.message : 'No se pudo eliminar el documento',
                                                    status: 'error',
                                                    duration: 5000,
                                                    isClosable: true,
                                                  });
                                                }
                                              }
                                            }}
                                          />
                                        </Stack>
                                      )}
                                    </Flex>
                                  </Box>
                                ))}
                              </VStack>
                            )}
                          </Box>
                        </VStack>
                      </TabPanel>
                    )}
                  </TabPanels>
                </Tabs>
              </CardBody>
            </Card>
          ) : null}
        </VStack>
      </Container>
    </Box>
  );
};

export default DetalleExpediente;
